import React, { useState, useRef, useEffect } from 'react';
import {
    Typography, Card, Button, Space, Form, Input, InputNumber, DatePicker,
    message, Row, Col, Radio, Upload, Modal, Progress, Tag, Switch,
    Popconfirm, Checkbox, Tooltip, Divider
} from 'antd';
import dayjs from 'dayjs';
import {
    SaveOutlined, PlusOutlined, DeleteOutlined, BookOutlined,
    PaperClipOutlined, FolderOutlined, FileTextOutlined, AppstoreOutlined,
    SendOutlined, InfoCircleOutlined, RightOutlined, DownOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createMoodleProgram, addManualModule, uploadFile } from '../services/moodleService';
import { getCurrentUserEmail } from '../services/trainingService';
import { TrainingType, NestedModule, NestedSubModule, NestedAssignment } from '../types/training';
import { validateBlobSafeName } from '../utils/moduleUtils';
import { defaultEvalCriteria, serializeEvalCriteria } from '../utils/evalCriteria';

const { Title, Text } = Typography;
const { TextArea } = Input;

const genId = () => Math.random().toString(36).substr(2, 9);

// New assignments start with the standard criteria checklist (editable).
const createEmptyAssignment = (): NestedAssignment => ({
    id: genId(), title: '', description: '', deadline: null, maxCredits: 0, evalCriteria: defaultEvalCriteria()
});
const createEmptySubModule = (): NestedSubModule => ({
    id: genId(), name: '', assignments: [createEmptyAssignment()]
});
const createEmptyModule = (): NestedModule => ({
    id: genId(), name: '', subModules: [], assignments: []
});

const addToSet = (s: Set<string>, ...items: string[]): Set<string> => {
    const n = new Set(Array.from(s));
    items.forEach(i => n.add(i));
    return n;
};

type SelectedNode =
    | { type: 'module'; moduleId: string }
    | { type: 'submodule'; moduleId: string; subId: string }
    | { type: 'assignment'; moduleId: string; subId: string; assignId: string }
    | { type: 'direct_assignment'; moduleId: string; assignId: string };

const CreateTrainerLedProgram: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [nestedModules, setNestedModules] = useState<NestedModule[]>([]);
    const [pendingFiles, setPendingFiles] = useState<Record<string, any[]>>({});

    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');
    const [percent, setPercent] = useState(0);

    const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
    const [openModuleIds, setOpenModuleIds] = useState<Set<string>>(new Set());
    const [openSubIds, setOpenSubIds] = useState<Set<string>>(new Set());

    const publishIntentRef = useRef(false);
    const treeBodyRef = useRef<HTMLDivElement>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (!selectedNode || !treeBodyRef.current) return;
        const nodeId = selectedNode.type === 'module' ? selectedNode.moduleId
            : selectedNode.type === 'submodule' ? selectedNode.subId
            : (selectedNode as any).assignId;
        const el = treeBodyRef.current.querySelector<HTMLElement>(`[data-node-id="${nodeId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selectedNode]);
    const navigate = useNavigate();

    // --- Module CRUD ---
    const addModule = () => {
        const m = createEmptyModule();
        setNestedModules(prev => [...prev, m]);
        setOpenModuleIds(prev => addToSet(prev, m.id));
        setSelectedNode({ type: 'module', moduleId: m.id });
    };
    const removeModule = (moduleId: string) => {
        setNestedModules(prev => prev.filter(m => m.id !== moduleId));
        if (selectedNode?.moduleId === moduleId) setSelectedNode(null);
    };
    const updateModuleName = (moduleId: string, name: string) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId ? { ...m, name } : m));

    // --- Sub-Module CRUD ---
    const addSubModule = (moduleId: string) => {
        const s = createEmptySubModule();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: [...(m.subModules || []), s] } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setOpenSubIds(prev => addToSet(prev, s.id));
        setSelectedNode({ type: 'submodule', moduleId, subId: s.id });
    };
    const removeSubModule = (moduleId: string, subId: string) => {
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).filter(s => s.id !== subId) } : m));
        if (selectedNode?.type === 'submodule' && selectedNode.subId === subId) setSelectedNode(null);
        if (selectedNode?.type === 'assignment' && selectedNode.subId === subId) setSelectedNode(null);
    };
    const updateSubModuleName = (moduleId: string, subId: string, name: string) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId ? { ...s, name } : s) } : m));

    // --- Assignment CRUD ---
    const addAssignment = (moduleId: string, subId: string) => {
        const a = createEmptyAssignment();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: [...(s.assignments || []), a] } : s) } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setOpenSubIds(prev => addToSet(prev, subId));
        setSelectedNode({ type: 'assignment', moduleId, subId, assignId: a.id });
    };
    const removeAssignment = (moduleId: string, subId: string, assignId: string) => {
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: (s.assignments || []).filter(a => a.id !== assignId) } : s) } : m));
        if (selectedNode?.type === 'assignment' && selectedNode.assignId === assignId) setSelectedNode(null);
    };
    const updateAssignment = (moduleId: string, subId: string, assignId: string, field: keyof NestedAssignment, value: any) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: (s.assignments || []).map(a => a.id === assignId ? { ...a, [field]: value } : a) } : s) } : m));

    // --- Direct Assignment CRUD ---
    const addDirectAssignment = (moduleId: string) => {
        const a = createEmptyAssignment();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: [...(m.assignments || []), a] } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setSelectedNode({ type: 'direct_assignment', moduleId, assignId: a.id });
    };
    const removeDirectAssignment = (moduleId: string, assignId: string) => {
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: (m.assignments || []).filter(a => a.id !== assignId) } : m));
        if (selectedNode?.type === 'direct_assignment' && selectedNode.assignId === assignId) setSelectedNode(null);
    };
    const updateDirectAssignment = (moduleId: string, assignId: string, field: keyof NestedAssignment, value: any) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: (m.assignments || []).map(a => a.id === assignId ? { ...a, [field]: value } : a) } : m));

    // --- Validation ---
    const validateHierarchy = (): boolean => {
        if (nestedModules.length === 0) { message.error('Please add at least one module.'); return false; }

        const seenModules = new Set<string>();
        for (const mod of nestedModules) {
            if (!mod.name.trim()) { message.error('Please provide names for all modules.'); return false; }

            const hasSubs = (mod.subModules || []).length > 0;
            const hasDirects = (mod.assignments || []).length > 0;
            if (!hasSubs && !hasDirects) {
                message.error(`Add at least one sub-module or direct assignment to "${mod.name}".`);
                return false;
            }

            const seenSubs = new Set<string>();
            for (const sub of (mod.subModules || [])) {
                if (!sub.name.trim()) { message.error(`Provide names for all sub-modules in "${mod.name}".`); return false; }
                const subKey = sub.name.trim().toLowerCase();
                if (seenSubs.has(subKey)) {
                    message.error(`Duplicate sub-module "${sub.name}" in "${mod.name}".`); return false;
                }
                seenSubs.add(subKey);
                if (!sub.assignments?.length) { message.error(`Add at least one assignment to "${mod.name} > ${sub.name}".`); return false; }
                const seenAssigns = new Set<string>();
                for (const a of sub.assignments) {
                    if (!a.title.trim()) { message.error(`Provide titles for all assignments in "${mod.name} > ${sub.name}".`); return false; }
                    if (!a.description.trim()) { message.error(`Provide a description for "${a.title}".`); return false; }
                    if (a.workflowEnabled && !a.deadline) { message.error(`Set a proposal deadline for "${a.title}".`); return false; }
                    if (!a.stateDeadlines?.completed) { message.error(`Set a final deadline for "${a.title}".`); return false; }
                    const aKey = a.title.trim().toLowerCase();
                    if (seenAssigns.has(aKey)) { message.error(`Duplicate assignment "${a.title}" in "${mod.name} > ${sub.name}".`); return false; }
                    seenAssigns.add(aKey);
                }
            }

            const seenDirects = new Set<string>();
            for (const a of (mod.assignments || [])) {
                if (!a.title.trim()) { message.error(`Provide titles for all direct assignments in "${mod.name}".`); return false; }
                if (!a.description.trim()) { message.error(`Provide a description for "${a.title}".`); return false; }
                if (a.workflowEnabled && !a.deadline) { message.error(`Set a proposal deadline for "${a.title}".`); return false; }
                if (!a.stateDeadlines?.completed) { message.error(`Set a final deadline for "${a.title}".`); return false; }
                const aKey = a.title.trim().toLowerCase();
                if (seenDirects.has(aKey)) { message.error(`Duplicate assignment "${a.title}" in "${mod.name}".`); return false; }
                seenDirects.add(aKey);
            }

            const modKey = mod.name.trim().toLowerCase();
            if (seenModules.has(modKey)) { message.error(`Duplicate module name "${mod.name}".`); return false; }
            seenModules.add(modKey);
        }
        return true;
    };

    // --- Submit ---
    const onFinish = async (values: any) => {
        if (!validateHierarchy()) return;
        try {
            setLoading(true);

            const programPayload = {
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                trainingType: values.trainingType || TrainingType.PRIVATE,
                passingPercentage: 100,
                estimatedEffortHours: values.estimatedEffortHours || 0,
                discussionEnabled: values.discussionEnabled !== false,
                audienceEmails: values.audienceEmails || '',
                stakeholderEmails: values.stakeholderEmails || '',
                trainerEmails: values.trainerEmails || '',
                trainingLink: values.trainingLink || '',
                startDate: values.dateRange ? values.dateRange[0].format('YYYY-MM-DD') : '',
                endDate: values.dateRange ? values.dateRange[1].format('YYYY-MM-DD') : '',
                programType: 'TrainerLed',
                visible: publishIntentRef.current ? 1 : 0,
                createdby: await getCurrentUserEmail()
            };

            let result: any;
            try {
                result = await createMoodleProgram(programPayload);
            } catch (createErr: any) {
                const msg: string = createErr?.message || String(createErr);
                if (msg.includes('already exists')) {
                    message.error(`Program ID "${values.trainingId}" is already in use. Please choose a different Training ID.`, 6);
                } else {
                    message.error(`Failed to create program: ${msg}`, 6);
                }
                setLoading(false);
                return;
            }

            if (result?.status === 'duplicate') {
                message.error(`Program ID "${values.trainingId}" is already in use. Please choose a different Training ID.`, 6);
                setLoading(false);
                return;
            }

            setProgressModalVisible(true);
            setProgressMessage('Creating training program...');
            setPercent(5);
            setProcessStatus('active');
            const courseId = result.id;

            let totalWork = 0;
            for (const mod of nestedModules) {
                totalWork += (mod.assignments || []).length;
                (mod.subModules || []).forEach(sub => { totalWork += (sub.assignments || []).length; });
                for (const a of [...(mod.assignments || []), ...(mod.subModules || []).flatMap(s => s.assignments || [])]) {
                    totalWork += (pendingFiles[a.id] || []).length;
                }
            }
            let done = 0;
            const updateProgress = () => setPercent(Math.round(10 + (done / Math.max(totalWork, 1)) * 80));

            const jsonMap: NestedModule[] = JSON.parse(JSON.stringify(nestedModules));
            let sectionNum = 1;

            for (const mod of jsonMap) {
                if (mod.assignments?.length) {
                    for (const assign of mod.assignments) {
                        setProgressMessage(`Creating: ${assign.title}...`);
                        const res = await addManualModule({
                            courseid: courseId,
                            title: assign.title,
                            content: assign.description,
                            deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                            sectionnum: sectionNum,
                            grade: assign.maxCredits ?? 0,
                            sectionname: `${mod.name}::__direct__`,
                            statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                            workflowenabled: assign.workflowEnabled ? 1 : 0,
                            evalcriteria: serializeEvalCriteria(assign.evalCriteria)
                        });
                        assign.moodleId = res.id;
                        for (const file of (pendingFiles[assign.id] || [])) {
                            const fileObj = file.originFileObj || file;
                            setProgressMessage(`Uploading: ${fileObj.name}...`);
                            await new Promise<void>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                    try {
                                        const base64 = (reader.result as string || '').split(',')[1] || '';
                                        await uploadFile({ fileName: fileObj.name, fileContent: base64, assignmentid: res.id, userid: await getCurrentUserEmail(), filearea: 'intro', filetype: fileObj.type, filesize: fileObj.size, trainingid: values.trainingId });
                                        done++; updateProgress(); resolve();
                                    } catch (err) { reject(err); }
                                };
                                reader.onerror = () => reject(new Error('Failed to read file'));
                                reader.readAsDataURL(fileObj);
                            });
                        }
                        assign.deadline = assign.deadline ? dayjs(assign.deadline).unix() : 0;
                        done++; updateProgress();
                    }
                    sectionNum++;
                }

                if (mod.subModules?.length) {
                    for (const sub of mod.subModules) {
                        for (const assign of sub.assignments) {
                            setProgressMessage(`Creating: ${assign.title}...`);
                            const res = await addManualModule({
                                courseid: courseId,
                                title: assign.title,
                                content: assign.description,
                                deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                                sectionnum: sectionNum,
                                grade: assign.maxCredits ?? 0,
                                sectionname: `${mod.name}::${sub.name}`,
                                statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                                workflowenabled: assign.workflowEnabled ? 1 : 0,
                                evalcriteria: serializeEvalCriteria(assign.evalCriteria)
                            });
                            assign.moodleId = res.id;
                            for (const file of (pendingFiles[assign.id] || [])) {
                                const fileObj = file.originFileObj || file;
                                setProgressMessage(`Uploading: ${fileObj.name}...`);
                                await new Promise<void>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = async () => {
                                        try {
                                            const base64 = (reader.result as string || '').split(',')[1] || '';
                                            await uploadFile({ fileName: fileObj.name, fileContent: base64, assignmentid: res.id, userid: await getCurrentUserEmail(), filearea: 'intro', filetype: fileObj.type, filesize: fileObj.size, trainingid: values.trainingId });
                                            done++; updateProgress(); resolve();
                                        } catch (err) { reject(err); }
                                    };
                                    reader.onerror = () => reject(new Error('Failed to read file'));
                                    reader.readAsDataURL(fileObj);
                                });
                            }
                            assign.deadline = assign.deadline ? dayjs(assign.deadline).unix() : 0;
                            done++; updateProgress();
                        }
                        sectionNum++;
                    }
                }
            }

            setPercent(100);
            setProcessStatus('success');
            setProgressMessage(publishIntentRef.current
                ? 'Program published successfully! Participants can now access it.'
                : 'Draft saved successfully! Publish the program when ready.');
            setTimeout(() => navigate('/admin'), 2000);

        } catch (error: any) {
            console.error('Error creating trainer-led program:', error);
            setProcessStatus('exception');
            setProgressMessage(`An error occurred: ${error.message || error}`);
        } finally {
            setLoading(false);
        }
    };

    // ── Tree helpers ──────────────────────────────────────────────────────────

    const toggleModule = (id: string) =>
        setOpenModuleIds(prev => { const n = new Set(Array.from(prev)); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleSub = (id: string) =>
        setOpenSubIds(prev => { const n = new Set(Array.from(prev)); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const treeNodeStyle = (selected: boolean, accent: string): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', padding: '5px 8px', borderRadius: 6,
        marginBottom: 2, cursor: 'pointer',
        background: selected ? accent + '22' : 'transparent',
        border: `1px solid ${selected ? accent : 'transparent'}`,
        transition: 'background 0.15s',
    });

    const renderAssignmentRow = (
        assign: NestedAssignment, idx: number,
        nodeType: 'assignment' | 'direct_assignment',
        moduleId: string, subId?: string, indent = 32
    ) => {
        const isSelected = nodeType === 'assignment'
            ? selectedNode?.type === 'assignment' && selectedNode.assignId === assign.id
            : selectedNode?.type === 'direct_assignment' && selectedNode.assignId === assign.id;
        const handleSelect = () => nodeType === 'direct_assignment'
            ? setSelectedNode({ type: 'direct_assignment', moduleId, assignId: assign.id })
            : setSelectedNode({ type: 'assignment', moduleId, subId: subId!, assignId: assign.id });
        const handleRemove = () => nodeType === 'direct_assignment'
            ? removeDirectAssignment(moduleId, assign.id)
            : removeAssignment(moduleId, subId!, assign.id);

        return (
            <div key={assign.id} data-node-id={assign.id} style={{ paddingLeft: indent }}>
                <div style={treeNodeStyle(isSelected, '#fa8c16')}>
                    <span style={{ width: 16 }} />
                    <div onClick={handleSelect} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}>
                        <FileTextOutlined style={{ color: '#faad14', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#d46b08' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {assign.title || `Assignment ${idx + 1}`}
                        </Text>
                    </div>
                    <Popconfirm title="Remove this assignment?" onConfirm={handleRemove} okText="Remove" okType="danger" cancelText="Cancel">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ flexShrink: 0, opacity: 0.5 }} onClick={e => e.stopPropagation()} />
                    </Popconfirm>
                </div>
            </div>
        );
    };

    const renderSubModuleRow = (mod: NestedModule, sub: NestedSubModule, subIdx: number) => {
        const isSelected = selectedNode?.type === 'submodule' && selectedNode.subId === sub.id;
        const isOpen = openSubIds.has(sub.id);
        return (
            <div key={sub.id} data-node-id={sub.id} style={{ paddingLeft: 16 }}>
                <div style={treeNodeStyle(isSelected, '#52c41a')}>
                    <span onClick={e => { e.stopPropagation(); toggleSub(sub.id); }}
                        style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#8c8c8c' }}>
                        {isOpen ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                    </span>
                    <div onClick={() => setSelectedNode({ type: 'submodule', moduleId: mod.id, subId: sub.id })}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}>
                        <FolderOutlined style={{ color: '#52c41a', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#389e0d' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            SM{subIdx + 1}: {sub.name || 'Untitled'}
                        </Text>
                        <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}>{(sub.assignments || []).length}</Tag>
                    </div>
                    <Space size={0} onClick={e => e.stopPropagation()}>
                        <Tooltip title="Add Assignment">
                            <Button type="text" size="small" icon={<PlusOutlined />} style={{ color: '#52c41a', opacity: 0.8 }}
                                onClick={() => addAssignment(mod.id, sub.id)} />
                        </Tooltip>
                        <Popconfirm title="Remove this sub-module and all its assignments?" onConfirm={() => removeSubModule(mod.id, sub.id)} okText="Remove" okType="danger" cancelText="Cancel">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ opacity: 0.5 }} />
                        </Popconfirm>
                    </Space>
                </div>
                {isOpen && (sub.assignments || []).map((a, ai) =>
                    renderAssignmentRow(a, ai, 'assignment', mod.id, sub.id, 16)
                )}
            </div>
        );
    };

    const renderModuleRow = (mod: NestedModule, modIdx: number) => {
        const isSelected = selectedNode?.type === 'module' && selectedNode.moduleId === mod.id;
        const isOpen = openModuleIds.has(mod.id);
        const totalAssignments = (mod.assignments || []).length +
            (mod.subModules || []).reduce((s, sub) => s + (sub.assignments || []).length, 0);

        return (
            <div key={mod.id} data-node-id={mod.id} style={{ marginBottom: 4 }}>
                <div style={treeNodeStyle(isSelected, '#1890ff')}>
                    <span onClick={e => { e.stopPropagation(); toggleModule(mod.id); }}
                        style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#8c8c8c' }}>
                        {isOpen ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                    </span>
                    <div onClick={() => setSelectedNode({ type: 'module', moduleId: mod.id })}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}>
                        <AppstoreOutlined style={{ color: '#1890ff', fontSize: 11, flexShrink: 0 }} />
                        <Text strong style={{ fontSize: 12, color: isSelected ? '#096dd9' : '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            M{modIdx + 1}: {mod.name || 'Untitled Module'}
                        </Text>
                        <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}>{totalAssignments}</Tag>
                    </div>
                    <Space size={0} onClick={e => e.stopPropagation()}>
                        <Tooltip title="Add Sub-Module">
                            <Button type="text" size="small" icon={<FolderOutlined />} style={{ color: '#52c41a', opacity: 0.8 }}
                                onClick={() => addSubModule(mod.id)} />
                        </Tooltip>
                        <Tooltip title="Add Direct Assignment">
                            <Button type="text" size="small" icon={<FileTextOutlined />} style={{ color: '#faad14', opacity: 0.8 }}
                                onClick={() => addDirectAssignment(mod.id)} />
                        </Tooltip>
                        <Popconfirm title="Remove this module and all its content?" onConfirm={() => removeModule(mod.id)} okText="Remove" okType="danger" cancelText="Cancel">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ opacity: 0.5 }} />
                        </Popconfirm>
                    </Space>
                </div>
                {isOpen && (
                    <>
                        {(mod.assignments || []).map((a, ai) =>
                            renderAssignmentRow(a, ai, 'direct_assignment', mod.id, undefined, 16)
                        )}
                        {(mod.subModules || []).map((sub, si) => renderSubModuleRow(mod, sub, si))}
                    </>
                )}
            </div>
        );
    };

    // ── Edit Panel ────────────────────────────────────────────────────────────

    const renderBreadcrumb = () => {
        if (!selectedNode) return null;
        const mod = nestedModules.find(m => m.id === selectedNode.moduleId);
        if (!mod) return null;
        const parts: React.ReactNode[] = [
            <Text key="mod" style={{ color: '#1890ff', fontWeight: 600 }}>
                <AppstoreOutlined style={{ marginRight: 4 }} />{mod.name || 'Untitled Module'}
            </Text>
        ];
        if (selectedNode.type === 'submodule' || selectedNode.type === 'assignment') {
            const sub = (mod.subModules || []).find(s => s.id === (selectedNode as any).subId);
            if (sub) {
                parts.push(<Text key="s1" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                parts.push(<Text key="sub" style={{ color: '#52c41a', fontWeight: 600 }}><FolderOutlined style={{ marginRight: 4 }} />{sub.name || 'Untitled'}</Text>);
                if (selectedNode.type === 'assignment') {
                    const a = (sub.assignments || []).find(a => a.id === (selectedNode as any).assignId);
                    if (a) {
                        parts.push(<Text key="s2" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                        parts.push(<Text key="a" style={{ color: '#fa8c16', fontWeight: 600 }}><FileTextOutlined style={{ marginRight: 4 }} />{a.title || 'Untitled'}</Text>);
                    }
                }
            }
        }
        if (selectedNode.type === 'direct_assignment') {
            const a = (mod.assignments || []).find(a => a.id === (selectedNode as any).assignId);
            if (a) {
                parts.push(<Text key="s1" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                parts.push(<Text key="a" style={{ color: '#fa8c16', fontWeight: 600 }}><FileTextOutlined style={{ marginRight: 4 }} />{a.title || 'Untitled'}</Text>);
            }
        }
        return <div style={{ marginBottom: 16, fontSize: 12, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{parts}</div>;
    };

    const renderEditPanel = () => {
        if (!selectedNode) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 380, color: '#bfbfbf' }}>
                    <BookOutlined style={{ fontSize: 48, marginBottom: 16, color: '#e8e8e8' }} />
                    <Text type="secondary">Select a module, sub-module, or assignment from the tree to edit it.</Text>
                </div>
            );
        }
        const mod = nestedModules.find(m => m.id === selectedNode.moduleId);
        if (!mod) return null;

        if (selectedNode.type === 'module') {
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Module Settings</Divider>
                    <Form.Item label="Module Name" required style={{ marginBottom: 16 }}>
                        <Input value={mod.name} onChange={e => updateModuleName(mod.id, e.target.value)}
                            placeholder="e.g. Action Learning" size="large" />
                    </Form.Item>
                    <Space>
                        <Button icon={<FolderOutlined />} onClick={() => addSubModule(mod.id)}>Add Sub-Module</Button>
                        <Button icon={<FileTextOutlined />} onClick={() => addDirectAssignment(mod.id)}>Add Direct Assignment</Button>
                    </Space>
                </>
            );
        }
        if (selectedNode.type === 'submodule') {
            const sub = (mod.subModules || []).find(s => s.id === selectedNode.subId);
            if (!sub) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Sub-Module Settings</Divider>
                    <Form.Item label="Sub-Module Name" required style={{ marginBottom: 16 }}>
                        <Input value={sub.name} onChange={e => updateSubModuleName(mod.id, sub.id, e.target.value)}
                            placeholder="e.g. Monthly Challenge" size="large" />
                    </Form.Item>
                    <Button icon={<PlusOutlined />} onClick={() => addAssignment(mod.id, sub.id)}>Add Assignment</Button>
                    {(sub.assignments || []).length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Assignments in this sub-module:</Text>
                            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {sub.assignments.map((a, ai) => (
                                    <Tag key={a.id} color="orange" style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedNode({ type: 'assignment', moduleId: mod.id, subId: sub.id, assignId: a.id })}>
                                        {a.title || `Assignment ${ai + 1}`}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            );
        }
        if (selectedNode.type === 'assignment') {
            const sub = (mod.subModules || []).find(s => s.id === selectedNode.subId);
            const assign = (sub?.assignments || []).find(a => a.id === selectedNode.assignId);
            if (!assign) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Assignment Details</Divider>
                    <AssignmentContent assign={assign} pendingFiles={pendingFiles}
                        onUpdate={(f, v) => updateAssignment(mod.id, selectedNode.subId, assign.id, f, v)}
                        onFilesChange={files => setPendingFiles(prev => ({ ...prev, [assign.id]: files }))} />
                </>
            );
        }
        if (selectedNode.type === 'direct_assignment') {
            const assign = (mod.assignments || []).find(a => a.id === selectedNode.assignId);
            if (!assign) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Assignment Details</Divider>
                    <AssignmentContent assign={assign} pendingFiles={pendingFiles}
                        onUpdate={(f, v) => updateDirectAssignment(mod.id, assign.id, f, v)}
                        onFilesChange={files => setPendingFiles(prev => ({ ...prev, [assign.id]: files }))} />
                </>
            );
        }
        return null;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={{ padding: '24px' }}>
            <Card style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: '12px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 8, backgroundColor: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AppstoreOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                    </div>
                    <Title level={4} style={{ margin: 0 }}>Create Trainer Led Program</Title>
                </div>
            </Card>

            <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ trainingType: TrainingType.PRIVATE, passingPercentage: 100, discussionEnabled: true }}>
                {/* ===== SECTION 1: Program Details ===== */}
                <Card title="1. Program Details" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={32}>
                        <Col span={12}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item label="Training ID" name="trainingId" style={{ marginBottom: 12 }}
                                        rules={[{ required: true, message: 'Please enter training ID' }, { validator: validateBlobSafeName }]}>
                                        <Input placeholder="e.g. TR-202X-001" size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={13}>
                                    <Form.Item label="Enrollment Type" name="trainingType" style={{ marginBottom: 12 }}>
                                        <Radio.Group optionType="button" buttonStyle="solid">
                                            <Radio value={TrainingType.PRIVATE}>Private (Invite Only)</Radio>
                                            <Radio value={TrainingType.OPEN}>Open (Anyone Can Join)</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item label="Discussion Panel" name="discussionEnabled" valuePropName="checked" style={{ marginBottom: 12 }}>
                                        <Switch checkedChildren="On" unCheckedChildren="Off" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Program Name" name="trainingName" style={{ marginBottom: 12 }}
                                        rules={[{ required: true, message: 'Please enter program name' }]}>
                                        <Input placeholder="e.g. Technology Leadership" size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Program Duration (Start - End)" name="dateRange" style={{ marginBottom: 12 }}
                                        rules={[{ required: true, message: 'Please select program duration' }]}>
                                        <DatePicker.RangePicker style={{ width: '100%' }} size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Effort (Hours)" name="estimatedEffortHours" style={{ marginBottom: 12 }}
                                        rules={[{ required: true, message: 'Required' }]}>
                                        <Input type="number" placeholder="e.g. 10" size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Resource Links (One per line)" name="trainingLink" style={{ marginBottom: 0 }}>
                                        <TextArea rows={3} placeholder="https://docs.microsoft.com..." />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={12}>
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.trainingType !== curr.trainingType}>
                                {({ getFieldValue }) => {
                                    const isOpen = getFieldValue('trainingType') === TrainingType.OPEN;
                                    return (
                                        <Form.Item label={`Participant Emails (Semicolon separated)${isOpen ? ' — optional for Open programs' : ''}`} name="audienceEmails" style={{ marginBottom: 12 }}
                                            rules={isOpen ? [] : [{ required: true, message: 'Please enter participant emails' }]}>
                                            <TextArea rows={3} placeholder="user1@company.com; user2@company.com" />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                            <Form.Item label="Stakeholder Emails (Semicolon separated)" name="stakeholderEmails" style={{ marginBottom: 12 }}
                                rules={[{ required: true, message: 'Please enter stakeholder emails' }]}>
                                <TextArea rows={3} placeholder="manager1@company.com" />
                            </Form.Item>
                            <Form.Item label="Trainer Emails (Semicolon separated)" name="trainerEmails" style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Please enter trainer emails' }]}>
                                <TextArea rows={3} placeholder="trainer1@company.com" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* ===== SECTION 2: Two-Panel Modules & Curriculum ===== */}
                <div style={{ marginBottom: 24 }}>
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        2. Modules & Curriculum <span style={{ color: '#ff4d4f' }}>*</span>
                    </Title>

                    {nestedModules.length === 0 ? (
                        <Card style={{ textAlign: 'center', padding: 40, backgroundColor: '#fafafa', borderStyle: 'dashed', borderRadius: 12 }}>
                            <BookOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <p style={{ color: '#8c8c8c' }}>No modules added yet. Click "Add Module" to get started.</p>
                            <Button type="primary" icon={<PlusOutlined />} onClick={addModule}>Add Module</Button>
                        </Card>
                    ) : (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', height: 600 }}>
                            <Card
                                style={{ width: 320, flexShrink: 0, borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                                bodyStyle={{ padding: '8px 6px', overflowY: 'auto', flex: 1 }}
                                title={<span style={{ fontSize: 13, color: '#595959' }}><AppstoreOutlined style={{ marginRight: 6 }} />Program Structure</span>}
                                extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={addModule}>Add Module</Button>}
                                size="small"
                            >
                                <div ref={treeBodyRef}>
                                    {nestedModules.map((mod, idx) => renderModuleRow(mod, idx))}
                                </div>
                            </Card>

                            <Card style={{ flex: 1, borderRadius: 12, display: 'flex', flexDirection: 'column' }} bodyStyle={{ padding: 24, flex: 1, overflowY: 'auto' }}>
                                {renderEditPanel()}
                            </Card>
                        </div>
                    )}
                </div>

                <div style={{ textAlign: 'right', marginTop: 32, marginBottom: 50 }}>
                    <Space size="large">
                        <Button onClick={() => navigate('/admin')} size="large">Cancel</Button>
                        <Button size="large" icon={<SaveOutlined />} loading={loading}
                            onClick={() => { publishIntentRef.current = false; form.submit(); }}>
                            Save as Draft
                        </Button>
                        <Button type="primary" size="large" icon={<SendOutlined />} loading={loading}
                            onClick={() => { publishIntentRef.current = true; form.submit(); }}>
                            Publish
                        </Button>
                    </Space>
                </div>
            </Form>

            <Modal title="Creation Progress" open={progressModalVisible}
                footer={[<Button key="close" onClick={() => setProgressModalVisible(false)} disabled={processStatus === 'active'}>Close</Button>]}
                closable={processStatus !== 'active'} maskClosable={false} centered>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Progress type="circle" percent={percent} status={processStatus}
                        strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} />
                    <div style={{ marginTop: 20, fontWeight: 500 }}>{progressMessage}</div>
                </div>
            </Modal>
        </div>
    );
};

export default CreateTrainerLedProgram;

// ===== Assignment Content =====
interface AssignmentContentProps {
    assign: NestedAssignment;
    pendingFiles: Record<string, any[]>;
    onUpdate: (field: keyof NestedAssignment, value: any) => void;
    onFilesChange: (files: any[]) => void;
}

const AssignmentContent: React.FC<AssignmentContentProps> = ({ assign, pendingFiles, onUpdate, onFilesChange }) => (
    <>
        <Row gutter={16}>
            <Col span={24}>
                <Form.Item label="Assignment Title" required style={{ marginBottom: 12 }}>
                    <Input value={assign.title} onChange={e => onUpdate('title', e.target.value)}
                        placeholder="e.g. Propose High Level Challenge" size="large" />
                </Form.Item>
            </Col>
            <Col span={24}>
                <Form.Item label="Description / Instructions" required style={{ marginBottom: 12 }}>
                    <Input.TextArea rows={3} value={assign.description}
                        onChange={e => onUpdate('description', e.target.value)}
                        placeholder="Describe the assignment, expectations, and deliverables..." />
                </Form.Item>
            </Col>
            {assign.workflowEnabled ? (
                <>
                    <Col span={8}>
                        <Form.Item label="Proposal Deadline" required style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.deadline ? dayjs(assign.deadline) : null}
                                onChange={val => onUpdate('deadline', val)} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Max Credits" required style={{ marginBottom: 12 }}>
                            <InputNumber min={0} max={1000} value={assign.maxCredits}
                                onChange={val => onUpdate('maxCredits', val ?? 0)} style={{ width: '100%' }} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Implementation Deadline" style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.stateDeadlines?.completed ? dayjs.unix(assign.stateDeadlines.completed as number) : null}
                                onChange={val => onUpdate('stateDeadlines', { ...(assign.stateDeadlines || {}), completed: val ? val.unix() : null })}
                                size="large" />
                        </Form.Item>
                    </Col>
                </>
            ) : (
                <>
                    <Col span={8}>
                        <Form.Item label="Max Credits" required style={{ marginBottom: 12 }}>
                            <InputNumber min={0} max={1000} value={assign.maxCredits}
                                onChange={val => onUpdate('maxCredits', val ?? 0)} style={{ width: '100%' }} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Final Deadline" style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.stateDeadlines?.completed ? dayjs.unix(assign.stateDeadlines.completed as number) : null}
                                onChange={val => onUpdate('stateDeadlines', { ...(assign.stateDeadlines || {}), completed: val ? val.unix() : null })}
                                size="large" />
                        </Form.Item>
                    </Col>
                </>
            )}
            <Col span={24}>
                <Form.Item style={{ marginBottom: 8 }}>
                    <Checkbox checked={!!assign.workflowEnabled} onChange={e => onUpdate('workflowEnabled', e.target.checked)}>
                        Enable workflow status (proposal → review → approval cycle)
                    </Checkbox>
                </Form.Item>
            </Col>
            <Col span={24}>
                <Form.Item label="Evaluation Criteria" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(assign.evalCriteria || []).map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Input
                                    placeholder={`Criterion ${i + 1} name`}
                                    value={c.name}
                                    onChange={e => {
                                        const updated = (assign.evalCriteria || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x);
                                        onUpdate('evalCriteria', updated);
                                    }}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    type="text" size="small" danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => onUpdate('evalCriteria', (assign.evalCriteria || []).filter((_, j) => j !== i))}
                                />
                            </div>
                        ))}
                        <Button
                            type="dashed" size="small" icon={<PlusOutlined />}
                            onClick={() => onUpdate('evalCriteria', [...(assign.evalCriteria || []), { name: '' }])}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Add Criterion
                        </Button>
                    </div>
                </Form.Item>
            </Col>
        </Row>

        <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary"><PaperClipOutlined /> Reference Materials</Text>
                <Upload fileList={pendingFiles[assign.id] || []} onChange={({ fileList }) => onFilesChange(fileList)} beforeUpload={() => false} multiple>
                    <Button icon={<PlusOutlined />} size="small">Attach Files</Button>
                </Upload>
            </div>
            {(pendingFiles[assign.id]?.length > 0) && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {pendingFiles[assign.id].length} file(s) queued for upload
                </div>
            )}
        </div>
    </>
);
